import {
  fetchAdminDashboard,
  fetchAdminFinanceTransactions,
  fetchAdminRevenueTrend,
  fetchAdminTopPosts,
  type AdminFinanceTx,
  type AdminRevenueDay,
  type AdminStats,
  type AdminTopPost,
} from "@/api/admin";
import { DonutChart, TrendChart } from "@/components/admin/AdminCharts";
import { AdminMediaPreview } from "@/components/admin/AdminMediaPreview";
import { AdminCard, MetricCard } from "@/components/admin/AdminShell";
import { ADMIN_NOT_IMPLEMENTED, formatCoins, formatInr, downloadCsv } from "@/components/admin/adminConstants";
import { Button } from "@/components/ui/Button";
import { useEffect, useState } from "react";

type Props = {
  stats: AdminStats;
};

export function AdminDashboardTab({ stats }: Props) {
  const [trend, setTrend] = useState<AdminRevenueDay[]>([]);
  const [topPosts, setTopPosts] = useState<AdminTopPost[]>([]);
  const [trendMetric, setTrendMetric] = useState<"wallet_inr_paise" | "promo_inr_paise" | "signups">(
    "wallet_inr_paise",
  );

  useEffect(() => {
    void fetchAdminRevenueTrend(7).then(setTrend).catch(() => undefined);
    void fetchAdminTopPosts(undefined, 8).then(setTopPosts).catch(() => undefined);
  }, []);

  const f = stats.finance ?? {};
  const totalRevenuePaise =
    (f.wallet_inr_total_paise ?? 0) + (f.promo_inr_total_paise ?? 0);
  const weekRevenuePaise = (f.wallet_inr_week_paise ?? 0) + (f.promo_inr_week_paise ?? 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Total revenue (INR)"
          value={formatInr(totalRevenuePaise)}
          sub="Wallet top-ups + paid promotions"
          trend={{
            value: `${formatInr(weekRevenuePaise)} this week`,
            positive: weekRevenuePaise > 0,
          }}
        />
        <MetricCard
          label="Wallet added (INR)"
          value={formatInr(f.wallet_inr_total_paise)}
          sub={`${formatInr(f.wallet_inr_today_paise)} today · ${formatCoins(f.wallet_coins_granted)} coins granted`}
        />
        <MetricCard
          label="Promotion spend (INR)"
          value={formatInr(f.promo_inr_total_paise)}
          sub={`${formatCoins(f.promo_sparks_total)} sparks on promos`}
        />
        <MetricCard
          label="Platform users"
          value={(stats.users?.total ?? 0).toLocaleString()}
          sub={`+${stats.users?.today ?? 0} today · ${stats.users?.week ?? 0} this week`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <AdminCard title="Content mix" className="lg:col-span-1">
          <DonutChart
            centerValue={(stats.posts?.ready ?? 0).toLocaleString()}
            centerLabel="Ready posts"
            segments={[
              { label: "Frames", value: stats.posts?.frames ?? 0, color: "#2d68c4" },
              { label: "Drifts", value: stats.posts?.drifts ?? 0, color: "#94a3b8" },
            ]}
          />
        </AdminCard>

        <AdminCard
          title="7-day trend"
          className="lg:col-span-2"
          action={
            <select
              className="rounded-lg border border-black/10 px-2 py-1 text-xs"
              value={trendMetric}
              onChange={(e) =>
                setTrendMetric(e.target.value as typeof trendMetric)
              }
            >
              <option value="wallet_inr_paise">Wallet top-ups</option>
              <option value="promo_inr_paise">Promotions</option>
              <option value="signups">Signups</option>
            </select>
          }
        >
          {trend.length ? (
            <TrendChart days={trend} metric={trendMetric} />
          ) : (
            <p className="text-sm text-ink-muted">Loading trend…</p>
          )}
        </AdminCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AdminCard title="Engagement & moderation">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              ["Comments", stats.engagement?.comments ?? 0],
              ["Follows", stats.engagement?.follows ?? 0],
              ["Chat (24h)", stats.engagement?.chat_messages_24h ?? 0],
              ["Open reports", stats.reports?.total_open ?? 0],
              ["Active promos", stats.promotions?.active ?? 0],
              ["Pending promos", stats.promotions?.pending ?? 0],
            ].map(([label, val]) => (
              <div key={String(label)} className="rounded-xl bg-[#f8f9fb] p-3">
                <div className="text-lg font-bold tabular-nums">{Number(val).toLocaleString()}</div>
                <div className="text-xs text-ink-muted">{label}</div>
              </div>
            ))}
          </div>
        </AdminCard>

        <AdminCard title="Creator economy (coins)">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#f8f9fb] p-3">
              <div className="text-lg font-bold tabular-nums">
                {formatCoins(f.tips_coins_total)}
              </div>
              <div className="text-xs text-ink-muted">Tips sent (all time)</div>
            </div>
            <div className="rounded-xl bg-[#f8f9fb] p-3">
              <div className="text-lg font-bold tabular-nums">
                {formatCoins(f.tips_coins_week)}
              </div>
              <div className="text-xs text-ink-muted">Tips this week</div>
            </div>
            <div className="rounded-xl bg-[#f8f9fb] p-3">
              <div className="text-lg font-bold tabular-nums">
                {f.active_subscriptions ?? 0}
              </div>
              <div className="text-xs text-ink-muted">Active Creator Pro subs</div>
            </div>
            <div className="rounded-xl bg-[#f8f9fb] p-3">
              <div className="text-lg font-bold tabular-nums">
                {formatCoins(f.wallet_balance_coins)}
              </div>
              <div className="text-xs text-ink-muted">Coins in user wallets</div>
            </div>
          </div>
        </AdminCard>
      </div>

      <AdminCard
        title="Top content by engagement"
        action={
          <span className="text-xs text-ink-muted">Vibes + comments · ready posts</span>
        }
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-black/[0.06] text-xs text-ink-muted">
                <th className="px-2 py-2 font-medium">Post</th>
                <th className="px-2 py-2 font-medium">Creator</th>
                <th className="px-2 py-2 font-medium">Type</th>
                <th className="px-2 py-2 font-medium">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {topPosts.map((p) => (
                <tr key={p.id} className="border-b border-black/[0.04] hover:bg-black/[0.02]">
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2">
                      <AdminMediaPreview post={p} className="!h-12 !w-[68px]" variant="auto" controls={p.is_drift} />
                      <span className="line-clamp-1 max-w-[180px] text-xs">
                        {p.caption || "(no caption)"}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-ink-muted">@{p.username ?? "—"}</td>
                  <td className="px-2 py-3">{p.is_drift ? "Drift" : "Frame"}</td>
                  <td className="px-2 py-3 font-medium tabular-nums">
                    {p.engagement ?? 0}
                    <span className="ml-1 text-xs font-normal text-ink-muted">
                      ({p.vibe_count ?? 0} / {p.comment_count ?? 0})
                    </span>
                  </td>
                </tr>
              ))}
              {!topPosts.length ? (
                <tr>
                  <td colSpan={4} className="px-2 py-6 text-center text-ink-muted">
                    No posts yet
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <AdminCard title="Still external-only">
        <p className="mb-3 text-sm text-ink-muted">
          Platform analytics (Sparks, Parallel, push, growth) are on the Platform tab. These items still need external APIs:
        </p>
        <ul className="grid gap-2 sm:grid-cols-2">
          {ADMIN_NOT_IMPLEMENTED.map((item) => (
            <li
              key={item.area}
              className="rounded-xl border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-sm"
            >
              <div className="font-medium text-amber-950">{item.area}</div>
              <div className="text-xs text-amber-900/80">{item.detail}</div>
            </li>
          ))}
        </ul>
      </AdminCard>
    </div>
  );
}

export function AdminFinanceTab({ stats }: Props) {
  const [txs, setTxs] = useState<AdminFinanceTx[]>([]);
  const f = stats.finance ?? {};

  useEffect(() => {
    void fetchAdminFinanceTransactions(40).then(setTxs).catch(() => undefined);
    void fetchAdminDashboard().then(() => undefined).catch(() => undefined);
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Wallet top-ups (all)" value={formatInr(f.wallet_inr_total_paise)} />
        <MetricCard label="Wallet this week" value={formatInr(f.wallet_inr_week_paise)} />
        <MetricCard label="Promotions (INR)" value={formatInr(f.promo_inr_total_paise)} />
        <MetricCard label="Promo sparks" value={formatCoins(f.promo_sparks_total)} />
      </div>

      <AdminCard title="Recent transactions">
        <div className="mb-3 flex justify-end">
          <Button
            variant="outline"
            onClick={() =>
              downloadCsv(
                "daywia-transactions.csv",
                ["when", "type", "user", "inr", "coins", "reference"],
                txs.map((t) => [
                  t.created_at,
                  t.tx_type,
                  t.username,
                  t.amount_inr_paise ? t.amount_inr_paise / 100 : "",
                  t.amount_coins,
                  t.reference,
                ]),
              )
            }
          >
            Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-black/[0.06] text-xs text-ink-muted">
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">User</th>
                <th className="px-2 py-2">INR</th>
                <th className="px-2 py-2">Coins</th>
                <th className="px-2 py-2">Ref</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={`${t.tx_type}-${t.id}`} className="border-b border-black/[0.04]">
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-ink-muted">
                    {new Date(t.created_at).toLocaleString()}
                  </td>
                  <td className="px-2 py-2 capitalize">{t.tx_type.replace("_", " ")}</td>
                  <td className="px-2 py-2">@{t.username ?? "—"}</td>
                  <td className="px-2 py-2 tabular-nums">
                    {t.amount_inr_paise ? formatInr(t.amount_inr_paise) : "—"}
                  </td>
                  <td className="px-2 py-2 tabular-nums">
                    {t.amount_coins ? formatCoins(t.amount_coins) : "—"}
                  </td>
                  <td className="max-w-[120px] truncate px-2 py-2 text-xs text-ink-muted">
                    {t.reference ?? "—"}
                  </td>
                </tr>
              ))}
              {!txs.length ? (
                <tr>
                  <td colSpan={6} className="px-2 py-8 text-center text-ink-muted">
                    No transactions yet
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </div>
  );
}
